import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchIdibuIframeHtml, fetchIdibuStatus } from "../../Redux/ActionCreators/idibuAction";
import {
  ContactGappeoModal,
  IdibuConnectForm,
  IdibuSettingsModal,
  WebhookUrlNotice,
} from "./IdibuConnect";
import "./idibuPostModal.scss";

/**
 * Job tab panel: loads authenticated HTML that auto-POSTs into the tenant's idibu iframe.
 * When the tenant has not connected idibu, admins connect it here (or contact Gappeo).
 */
function IdibuPostTab() {
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [frameUrl, setFrameUrl] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [connectNotice, setConnectNotice] = useState(null);

  const loadFrame = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError("");
    setFrameUrl("");
    const result = await dispatch(fetchIdibuIframeHtml(jobId));
    if (result?.success && result.html) {
      setFrameUrl(URL.createObjectURL(new Blob([result.html], { type: "text/html" })));
    } else {
      setError(result?.message || "Failed to open Idibu posting");
    }
    setLoading(false);
  }, [dispatch, jobId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const s = await dispatch(fetchIdibuStatus());
    setStatus(s || {});
    if (s?.effective_enabled) {
      await loadFrame();
    } else {
      setFrameUrl("");
      setLoading(false);
    }
  }, [dispatch, loadFrame]);

  useEffect(() => {
    if (jobId) refresh();
  }, [jobId, refresh]);

  useEffect(() => {
    return () => {
      if (frameUrl) URL.revokeObjectURL(frameUrl);
    };
  }, [frameUrl]);

  const connected = Boolean(status?.connected);
  const canPost = Boolean(status?.effective_enabled);
  const canManage = Boolean(status?.can_manage);

  const handleConnected = async (data) => {
    if (data?.status !== "active") {
      setConnectNotice({
        severity: "warning",
        text: data?.last_error || "idibu details saved, but they could not be verified yet. Try again shortly.",
        webhookUrl: data?.webhook_url,
      });
      return;
    }
    setConnectNotice({ severity: "success", text: "idibu connected.", webhookUrl: data?.webhook_url });
    await refresh();
  };

  const renderConnect = () => (
    <div className="idibu-connect-inline">
      <h4>Connect idibu to post this job to job boards</h4>
      <p className="idibu-muted">Job board posting uses your organization's own idibu account.</p>
      {connectNotice?.severity === "warning" ? (
        <div className="idibu-inline-alert warning">{connectNotice.text}</div>
      ) : null}
      <IdibuConnectForm integration={null} onSaved={handleConnected} submitLabel="Connect" />
      <div className="idibu-contact-row">
        {status?.request_pending ? (
          <span className="idibu-success-text">Your request has been sent. The Gappeo team will contact you.</span>
        ) : (
          <>
            <span className="idibu-muted">Don't have an idibu account?</span>
            <button type="button" className="idibu-link" onClick={() => setShowContact(true)}>
              Contact Gappeo
            </button>
          </>
        )}
      </div>
    </div>
  );

  const renderNotReady = () => {
    if (!connected && canManage) return renderConnect();
    const message = !connected
      ? "idibu is not connected for your organization. Ask your admin to connect it."
      : status?.reason || "You don't have access to post jobs to job boards. Ask your admin for access.";
    return (
      <div className="idibu-modal-state">
        <p className="idibu-muted" style={{ maxWidth: 480, fontSize: 14 }}>{message}</p>
      </div>
    );
  };

  return (
    <div className="idibu-post-tab">
      <div className="idibu-post-tab-header">
        <div>
          <h3>Post to Job Boards</h3>
          <p>Select boards and publish this job inside Idibu.</p>
        </div>
        <div className="idibu-post-tab-actions">
          {error && canPost ? (
            <button type="button" className="idibu-btn-secondary" onClick={loadFrame}>
              Retry
            </button>
          ) : null}
          {connected && canManage ? (
            <button type="button" className="idibu-btn-secondary" onClick={() => setShowSettings(true)}>
              idibu settings
            </button>
          ) : null}
        </div>
      </div>
      {connectNotice?.severity === "success" && connectNotice.webhookUrl ? (
        <div className="idibu-connected-banner">
          <div className="idibu-connected-banner-head">
            <strong>idibu connected.</strong>
            <button type="button" className="idibu-link" onClick={() => setConnectNotice(null)}>
              Dismiss
            </button>
          </div>
          <WebhookUrlNotice url={connectNotice.webhookUrl} />
        </div>
      ) : null}
      <div className="idibu-post-tab-body">
        {loading ? (
          <div className="idibu-modal-state">
            <div className="spinner-border" role="status" style={{ color: "#F75891" }}>
              <span className="visually-hidden">Loading…</span>
            </div>
            <p>Opening Idibu…</p>
          </div>
        ) : null}
        {!loading && status && !canPost ? renderNotReady() : null}
        {!loading && canPost && error ? (
          <div className="idibu-modal-state idibu-modal-error">
            <p>{error}</p>
          </div>
        ) : null}
        {!loading && canPost && !error && frameUrl ? (
          <iframe title="Idibu posting" className="idibu-frame" src={frameUrl} />
        ) : null}
      </div>
      {showContact ? (
        <ContactGappeoModal
          onClose={() => setShowContact(false)}
          onSent={() => setStatus((prev) => ({ ...prev, request_pending: true }))}
        />
      ) : null}
      {showSettings ? (
        <IdibuSettingsModal onClose={() => setShowSettings(false)} onChanged={refresh} />
      ) : null}
    </div>
  );
}

export default IdibuPostTab;
